import React, { useEffect, useState } from 'react';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { fullScreenPlugin } from '@react-pdf-viewer/full-screen';
import '@react-pdf-viewer/full-screen/lib/styles/index.css';
import mammoth from 'mammoth';

const ResumeViewer = ({ fileUrl }) => {
  const [docxHtml, setDocxHtml] = useState('');
  const [fileType, setFileType] = useState(null); // 'pdf' | 'docx' | 'doc' | 'unknown'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fullScreenPluginInstance = fullScreenPlugin();

  useEffect(() => {
    if (!fileUrl) {
      setLoading(false);
      return;
    }

    const cleanUrl = fileUrl.split('?')[0]; // strip query params before checking extension
    const ext = cleanUrl.split('.').pop().toLowerCase();

    setError(null);
    setDocxHtml('');
    setLoading(true);

    if (ext === 'pdf') {
      setFileType('pdf');
      // Verify accessibility before handing to the PDF viewer,
      // so a 403 shows a clear message instead of a viewer render error
      fetch(fileUrl, { method: 'HEAD' })
        .then((res) => {
          if (!res.ok) {
            throw new Error(
              res.status === 403
                ? "Access denied — this file isn't accessible (403 Forbidden)."
                : `Failed to load file (status ${res.status}).`
            );
          }
        })
        .catch((err) => {
          console.error('Error checking PDF:', err);
          setError(err.message || 'Failed to load PDF.');
        })
        .finally(() => setLoading(false));
    } else if (ext === 'docx') {
      setFileType('docx');
      fetch(fileUrl)
        .then((response) => {
          if (!response.ok) {
            throw new Error(
              response.status === 403
                ? "Access denied — this file isn't accessible (403 Forbidden)."
                : `Failed to load file (status ${response.status}).`
            );
          }
          return response.arrayBuffer();
        })
        .then((arrayBuffer) => mammoth.convertToHtml({ arrayBuffer }))
        .then((result) => {
          setDocxHtml(result.value);
        })
        .catch((err) => {
          console.error('Error rendering DOCX:', err);
          setError(err.message || 'Failed to render document.');
        })
        .finally(() => setLoading(false));
    } else if (ext === 'doc') {
      // Legacy binary .doc can't be parsed client-side (mammoth only supports .docx)
      setFileType('doc');
      setError(
        'Legacy .doc files can\'t be previewed in-browser. Please download to view, or convert to PDF/DOCX.'
      );
      setLoading(false);
    } else {
      setFileType('unknown');
      setError(`Unsupported file type: .${ext}`);
      setLoading(false);
    }
  }, [fileUrl]);

  if (loading) {
    return (
      <div style={{ height: '750px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #ccc' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading document...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ height: '750px', padding: '1rem', border: '1px solid #ccc' }}>
        <div className="alert alert-danger mb-3">
          <strong>Couldn't load resume:</strong> {error}
        </div>
        <a href={fileUrl} target="_blank" rel="noopener noreferrer">
          Try opening the file directly
        </a>
      </div>
    );
  }

  return (
    <div style={{ height: '750px', overflowY: 'auto', padding: '1rem', border: '1px solid #ccc' }}>
      {fileType === 'pdf' && (
        <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.7.107/build/pdf.worker.min.js">
          <Viewer fileUrl={fileUrl} plugins={[fullScreenPluginInstance]} />
        </Worker>
      )}
      {fileType === 'docx' && (
        <div dangerouslySetInnerHTML={{ __html: docxHtml }} />
      )}
    </div>
  );
};

export default ResumeViewer;